/* Small site-wide behaviours of the Laserlab theme, loaded on every page (28.09):
   1. The star: one movement for every star on the site.
      The exception: the star in SKINLAB* on the Skinlab poster ticks like a second hand (1b).
   2. Rows that scroll sideways (the chips on every list): one line, a fade only where more waits, a mouse can drag.
   3. Labels that must stay on one line: the short name when the full name does not fit.
   4. The cookie link Shopify adds to the footer, in Norwegian. */
(() => {
  if (window.LaserlabSite) return;
  window.LaserlabSite = true;

  /* ---------- 1. The star ----------
     The star is the only thing on the site that moves, and only when the visitor does: pointing at it (mouse) or
     pressing it (touch) turns it once, 360 degrees in .9 s, fast then soft. A turn always finishes (leaving mid-turn
     never snaps it back) and never restarts halfway. With «reduce motion» nothing turns. Hosts carry data-ll-star: the
     star itself (list heads) or the element whose ::after draws it (the consultation card, SKINLAB*). The header
     logo has its own script (laserlab-logo-scroll.js), since it also follows scrolling.
     The exception: the star in SKINLAB* on the Skinlab poster ticks like a second hand (1b). */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const starOf = target => {
    const host = target instanceof Element ? target.closest('[data-ll-star]') : null;
    return host && host.dataset.llStar !== 'clock' ? host : null;
  };
  const turn = star => {
    if (!star || reduceMotion.matches || star.classList.contains('is-turning')) return;
    star.classList.add('is-turning');
  };
  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse') return;
    const star = starOf(event.target);
    if (star && !star.contains(event.relatedTarget)) turn(star);
  }, { passive: true });
  document.addEventListener('pointerdown', event => turn(starOf(event.target)), { passive: true });
  document.addEventListener('animationend', event => {
    if (event.animationName === 'll-star-turn') starOf(event.target)?.classList.remove('is-turning');
  });

  /* ---------- 1b. The clock star ----------
     One star on the site keeps time: SKINLAB* on the Skinlab poster (data-ll-star="clock"). It steps once a second on
     the wall clock, like a quartz second hand. The step and direction are CSS (--ll-star-step, --ll-star-dir); this
     only counts seconds in --ll-star-tick, and the count only grows, so a step never turns backwards. It runs only
     while the star is on screen, the tab is visible, no dialog is open, the visitor has not paused it (the toggle over
     the star, WCAG 2.2.2) and «reduce motion» is off. With «reduce motion» the star stands still and the toggle is
     hidden. It never catches up: after a pause it jumps back to the wall clock without turning. */
  const clocks = new Map();
  const dialogOpen = () => { try { return !!document.querySelector(':modal'); } catch (error) { return !!document.querySelector('dialog[open]'); } };
  const running = clock => !clock.paused && clock.visible && !document.hidden && !reduceMotion.matches && !dialogOpen();
  const stopClock = clock => { clearTimeout(clock.timer); clock.timer = 0; };
  const showTick = (clock, tick, jump) => {
    clock.tick = tick;
    if (jump) clock.host.classList.add('is-syncing');
    clock.host.style.setProperty('--ll-star-tick', String(tick));
    if (jump) requestAnimationFrame(() => requestAnimationFrame(() => clock.host.classList.remove('is-syncing')));
  };
  const scheduleTick = clock => {
    clock.timer = setTimeout(() => {
      clock.timer = 0;
      if (!running(clock)) return;
      showTick(clock, clock.tick + 1, false);
      scheduleTick(clock);
    }, 1000 - (Date.now() % 1000) + 8);
  };
  const syncClock = clock => {
    stopClock(clock);
    if (clock.toggle) clock.toggle.hidden = reduceMotion.matches;
    if (reduceMotion.matches) { clock.host.style.removeProperty('--ll-star-tick'); return; }
    if (!running(clock)) return;
    showTick(clock, Math.floor(Date.now() / 1000) % 60, true);
    scheduleTick(clock);
  };
  const clockSeen = new IntersectionObserver(entries => entries.forEach(({ target, isIntersecting }) => {
    const clock = clocks.get(target);
    if (clock) { clock.visible = isIntersecting; syncClock(clock); }
  }));
  const watchClock = host => {
    if (clocks.has(host)) return;
    const toggle = host.id ? document.querySelector(`[data-ll-star-pause][aria-controls="${host.id}"]`) : null;
    const clock = { host, toggle, tick: 0, timer: 0, visible: false, paused: false };
    clocks.set(host, clock);
    clockSeen.observe(host);
    toggle?.addEventListener('click', () => {
      clock.paused = !clock.paused;
      toggle.setAttribute('aria-pressed', String(clock.paused));
      syncClock(clock);
    });
    syncClock(clock);
  };
  const syncClocks = () => clocks.forEach(clock => {
    if (clock.host.isConnected) { syncClock(clock); return; }
    stopClock(clock); clockSeen.unobserve(clock.host); clocks.delete(clock.host);
  });
  document.addEventListener('visibilitychange', syncClocks);
  reduceMotion.addEventListener('change', syncClocks);
  window.matchMedia('(min-width: 768px)').addEventListener('change', syncClocks);
  window.addEventListener('pageshow', syncClocks);
  document.addEventListener('close', syncClocks, true);   /* a dialog closed (close does not bubble; capture sees it) */

  /* ---------- 2. Rows that scroll sideways ----------
     The chips are one line at every width (the owner: «alltid på én linje, så man må scrolle / dra»). Each row says
     where more chips wait (data-more-start / data-more-end) and the stylesheet fades only that edge, so a row that
     fits never fades. Touch swipes as always; a mouse drags the row, and a drag never presses the chip it ends on. */
  const ROW = '.ll-tfilter__chips, [data-ll-scroll-row]';
  const rows = new WeakSet();
  const edges = row => {
    const max = row.scrollWidth - row.clientWidth;
    row.toggleAttribute('data-more-start', max > 1 && row.scrollLeft > 1);
    row.toggleAttribute('data-more-end', max > 1 && row.scrollLeft < max - 1);
  };
  const rowSizes = new ResizeObserver(entries => {
    entries.forEach(({ target }) => {
      const row = rows.has(target) ? target : target.parentElement;
      if (row && rows.has(row)) edges(row);
    });
  });
  const watchRow = row => {
    if (rows.has(row)) return;
    rows.add(row);
    row.addEventListener('scroll', () => edges(row), { passive: true });
    rowSizes.observe(row);
    Array.from(row.children).forEach(child => rowSizes.observe(child));
    edges(row);
  };
  let drag = null;
  document.addEventListener('pointerdown', event => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const row = event.target instanceof Element ? event.target.closest(ROW) : null;
    if (!row || row.scrollWidth - row.clientWidth <= 1) return;
    drag = { row, id: event.pointerId, x: event.clientX, left: row.scrollLeft, moved: false };
  });
  document.addEventListener('pointermove', event => {
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      drag.row.classList.add('is-dragging');
      try { drag.row.setPointerCapture(event.pointerId); } catch (error) { /* the row still follows the pointer */ }
    }
    drag.row.scrollLeft = drag.left - dx;
  });
  const endDrag = event => {
    if (!drag || event.pointerId !== drag.id) return;
    const { row, moved } = drag;
    drag = null;
    if (!moved) return;
    row.classList.remove('is-dragging');
    const swallow = click => { click.preventDefault(); click.stopPropagation(); };
    row.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => row.removeEventListener('click', swallow, { capture: true }), 0);
  };
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
  document.addEventListener('dragstart', event => {
    if (event.target instanceof Element && event.target.closest(ROW)) event.preventDefault();
  });

  /* ---------- 3. Labels that must stay on one line ----------
     A badge with data-ll-short (a skin need on a card; the owner: «forkort tags … hvis man ikke får alt på en linje»)
     shows its full name when that fits on one line, and its short name, one word, when it does not. The card is
     watched, not the badge, so a shorter name never sets off a new measurement. */
  const labels = new Set();
  let fitting = false;
  const fitLabels = () => {
    fitting = false;
    const list = [...labels].filter(label => label.isConnected);
    list.forEach(label => {
      if (!label.dataset.llLong) label.dataset.llLong = label.textContent.trim();
      if (label.textContent !== label.dataset.llLong) label.textContent = label.dataset.llLong;
    });
    list.filter(label => label.scrollWidth > label.clientWidth + 1).forEach(label => { label.textContent = label.dataset.llShort; });
  };
  const queueFit = () => {
    if (fitting) return;
    fitting = true;
    requestAnimationFrame(fitLabels);
  };
  const cardSizes = new ResizeObserver(queueFit);
  const watchLabel = label => {
    if (labels.has(label) || !label.dataset.llShort) return;
    labels.add(label);
    cardSizes.observe(label.closest('.product-card, [data-ll-fit-scope]') || label.parentElement);
    queueFit();
  };

  /* ---------- 4. The cookie link Shopify adds to the footer ----------
     Shopify's consent script appends «Cookie preferences» in English to the policy links; it says it in Norwegian. */
  const COOKIE_LINK = 'a[href="#shopifyReshowConsentBanner"]';
  const nameCookieLink = link => { if (link.textContent.trim() !== 'Informasjonskapsler') link.textContent = 'Informasjonskapsler'; };

  const scan = root => {
    if (root instanceof Element) {
      if (root.matches(ROW)) watchRow(root);
      if (root.matches('[data-ll-short]')) watchLabel(root);
      if (root.matches(COOKIE_LINK)) nameCookieLink(root);
      if (root.matches('[data-ll-star="clock"]')) watchClock(root);
    } else if (!(root instanceof Document)) return;
    root.querySelectorAll(ROW).forEach(watchRow);
    root.querySelectorAll('[data-ll-short]').forEach(watchLabel);
    root.querySelectorAll(COOKIE_LINK).forEach(nameCookieLink);
    root.querySelectorAll('[data-ll-star="clock"]').forEach(watchClock);
  };
  scan(document);
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(scan))).observe(document.body, { childList: true, subtree: true });
  document.fonts?.ready.then(() => {
    queueFit();
    document.querySelectorAll(ROW).forEach(row => { if (rows.has(row)) edges(row); });
  });
})();
